import { type PersonOption } from '@/components/global/PersonSelect';
import { SheetPickerList } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useTranslations } from '@/hooks/useTranslations';
import axios from '@/lib/axios';
import { PRIORITY_ROLES, roleNameOf, type AssignableUser } from '@/lib/employee-roles';
import { User } from '@/types';
import { router } from '@inertiajs/react';
import { Loader2, Users } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

interface Props {
    companyId: string;
    companyUsers: User[];
    trigger?: ReactNode;
    /** Set to drive the modal from the page — see useControllableOpen. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function AssignUserToCompany({ companyId, companyUsers: initialCompanyUsers = [], trigger, open: openProp, onOpenChange }: Props) {
    const { t } = useTranslations();
    const [isOpen, setIsOpen] = useControllableOpen(openProp, onOpenChange);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>(initialCompanyUsers.map((u) => u.id));
    const [saving, setSaving] = useState(false);
    const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    useEffect(() => {
        setSelectedUserIds(initialCompanyUsers.map((u) => u.id));
    }, [initialCompanyUsers]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedUserIds(initialCompanyUsers.map((u) => u.id));
        }
    }, [isOpen, initialCompanyUsers]);

    // Everyone active is eligible; the picker only reorders them.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let cancelled = false;

        void (async () => {
            setLoadingUsers(true);
            try {
                const response = await axios.get('/api/users/assignable', { params: { limit: 500 } });
                if (cancelled) {
                    return;
                }
                setAssignableUsers(Array.isArray(response.data?.users) ? response.data.users : []);
            } catch {
                // The already-assigned users below still render, so this stays quiet.
            } finally {
                if (!cancelled) {
                    setLoadingUsers(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    /**
     * Whoever is already assigned stays in the list even if the fetch has not
     * landed (or dropped them), so a selection is never rendered as blank.
     */
    const people: PersonOption[] = useMemo(() => {
        const byId = new Map<string, PersonOption>();

        [...assignableUsers, ...initialCompanyUsers].forEach((user) => {
            const id = String((user as { uuid?: string }).uuid ?? user.id ?? '');
            if (!id) {
                return;
            }
            byId.set(id, {
                id,
                first_name: user.first_name ?? '',
                last_name: user.last_name ?? '',
                avatar: user.avatar ?? null,
                role: roleNameOf(user),
            });
        });

        return Array.from(byId.values());
    }, [assignableUsers, initialCompanyUsers]);

    /**
     * Commercial roles first — they are who a company is normally assigned to —
     * then everyone else, each under its own heading.
     */
    const options = useMemo(() => {
        const suggestedHeading = t('companies.forms.assign_user.suggested', 'Sales & project managers');
        const othersHeading = t('companies.forms.assign_user.others', 'Other colleagues');

        const toOption = (person: PersonOption, group: string) => ({
            id: person.id,
            label: [person.first_name, person.last_name].filter(Boolean).join(' ').trim() || person.id,
            keywords: person.role ?? undefined,
            badge: person.role ? <Badge variant="secondary">{person.role}</Badge> : null,
            group,
        });

        const priority = people.filter((person) => PRIORITY_ROLES.includes(person.role ?? ''));
        const rest = people.filter((person) => !PRIORITY_ROLES.includes(person.role ?? ''));

        return [...priority.map((person) => toOption(person, suggestedHeading)), ...rest.map((person) => toOption(person, othersHeading))];
    }, [people, t]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const toAdd = selectedUserIds.filter((id) => !initialCompanyUsers.some((u) => u.id === id));
            const toRemove = initialCompanyUsers.filter((u) => !selectedUserIds.includes(u.id)).map((u) => u.id);

            await Promise.all([
                ...toRemove.map((id) => axios.delete(`/companies/${companyId}/remove-user/${id}`)),
                ...toAdd.map((id) => axios.post(`/companies/${companyId}/assign-user`, { user_id: id })),
            ]);

            toast.success(t('companies.forms.assign_user.toasts.updated', 'Employee assignments updated!'));
            setIsOpen(false);
            router.reload();
        } catch {
            toast.error(t('companies.forms.assign_user.toasts.failed', 'Failed to update employee assignments'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : openProp === undefined ? (
                // Only when the modal owns its open state: a page driving it
                // supplies its own buttons and must not get a stray one here.
                <ResponsiveModalTrigger asChild>
                    <Button variant="outline">
                        <Users className="mr-2 h-4 w-4" />
                        {t('companies.forms.assign_user.buttons.assign_employee', 'Assign employee')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('companies.forms.assign_user.title', 'Employee Assignments')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'companies.forms.assign_user.description',
                            'Select or deselect employees to assign them to this company. Click Save to apply changes.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                    {/* Listed in the sheet rather than behind a combobox, like the
                        other assignment sheets. */}
                    <SheetPickerList
                        multiple
                        values={selectedUserIds}
                        onValuesChange={setSelectedUserIds}
                        options={options}
                        loading={loadingUsers && options.length === 0}
                        labels={{
                            search: t('companies.forms.assign_user.search', 'Search employees…'),
                            empty: t('companies.forms.assign_user.empty', 'No employees found.'),
                        }}
                    />
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => setIsOpen(false)} disabled={saving}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
